import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ChevronDown, ChevronUp, Pencil, Check, X, Link2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtDate = (d) => {
  if (!d) return "–";
  const p = String(d).split("-");
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : d;
};
const fmtEuro = (v) => (v > 0 ? `${new Intl.NumberFormat("de-DE").format(v)} EUR` : "–");

export default function OrderList({ orders = [], onDataChange }) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [eKunde, setEKunde] = useState("");
  const [eProjekt, setEProjekt] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const active = orders
    .filter((o) => o.status === "active")
    .sort((a, b) => (b.decision_date || "").localeCompare(a.decision_date || ""));
  const inactive = orders
    .filter((o) => o.status !== "active")
    .sort((a, b) => (b.decision_date || "").localeCompare(a.decision_date || ""));

  const startEdit = (o) => {
    setEditId(o.id);
    setEKunde(o.kunde || "");
    setEProjekt(o.projekt || "");
  };

  const save = async (id) => {
    try {
      await api.patch(`/orders/${id}/client`, { kunde: eKunde.trim(), projekt: eProjekt.trim() });
      toast.success("Auftrag aktualisiert");
      setEditId(null);
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await api.delete(`/orders/${deleteTarget.id}/permanent`);
      const pd = res.data?.points_delta;
      toast.success(
        res.data?.entry_adjusted && pd
          ? `Auftrag gelöscht. Tagespunkte angepasst (${pd > 0 ? "+" : ""}${pd}).`
          : "Auftrag endgültig gelöscht"
      );
      setDeleteTarget(null);
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const inputCls =
    "w-full h-8 bg-zinc-950 border border-zinc-700 text-xs text-zinc-100 px-2 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-500";

  const Row = ({ o, cancelled }) => {
    const unassigned = !o.kunde;
    const editable = !cancelled && o.offer_valid !== true;
    if (editId === o.id) {
      return (
        <div className="border border-zinc-700 bg-zinc-950/60 p-2.5 space-y-2" data-testid={`order-edit-${o.id.slice(0, 8)}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={eKunde} onChange={(e) => setEKunde(e.target.value)} placeholder="Kunde" className={inputCls} data-testid="order-edit-kunde" />
            <input value={eProjekt} onChange={(e) => setEProjekt(e.target.value)} placeholder="Projekt" className={inputCls} data-testid="order-edit-projekt" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" className="h-7 text-[10px] text-zinc-300" onClick={() => setEditId(null)} data-testid="order-edit-cancel">
              <X className="h-3 w-3 mr-1" />Abbrechen
            </Button>
            <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => save(o.id)} data-testid="order-edit-save">
              <Check className="h-3 w-3 mr-1" />Speichern
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className={`flex items-center gap-3 border p-2.5 ${cancelled ? "border-zinc-800/30 bg-zinc-950/20 opacity-70" : "border-zinc-800/50 bg-zinc-950/30"}`} data-testid={`order-row-${o.id.slice(0, 8)}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {o.offer_valid === true && <Link2 className="h-3 w-3 text-cyan-400 shrink-0" />}
            {cancelled && <span className="text-[8px] uppercase text-zinc-500 border border-zinc-800 px-1">storniert</span>}
            <p className={`text-xs truncate ${unassigned ? "text-amber-400/90 italic" : "text-zinc-100"}`}>
              {o.kunde || "Kunde noch nicht zugeordnet"}
            </p>
          </div>
          <p className="text-[10px] text-zinc-400 truncate">{o.projekt || (unassigned ? "Projekt offen" : "–")}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] text-zinc-300 font-mono">{fmtDate(o.decision_date)}</p>
          <p className="text-[10px] text-emerald-400 font-mono">{fmtEuro(o.auftragswert)}</p>
        </div>
        {editable && (
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-400 hover:text-white shrink-0" onClick={() => startEdit(o)} data-testid={`order-edit-btn-${o.id.slice(0, 8)}`}>
            <Pencil className="h-3 w-3" />
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400/80 hover:text-red-400 shrink-0" onClick={() => setDeleteTarget(o)} data-testid={`order-delete-btn-${o.id.slice(0, 8)}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    );
  };

  return (
    <div className="mt-4 border-t border-zinc-800/60 pt-3" data-testid="cockpit-orders">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-300 hover:text-white transition-colors" data-testid="orders-toggle">
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        Aufträge anzeigen ({active.length})
      </button>

      {open && (
        <div className="mt-3 space-y-1.5" data-testid="orders-panel">
          {active.length === 0 ? (
            <p className="text-zinc-500 text-xs py-2">Keine aktiven Aufträge.</p>
          ) : (
            active.map((o) => <Row key={o.id} o={o} cancelled={false} />)
          )}

          {inactive.length > 0 && (
            <details className="mt-2">
              <summary className="text-[10px] text-zinc-400 cursor-pointer hover:text-zinc-200 py-1">
                {inactive.length} stornierte / deaktivierte Aufträge
              </summary>
              <div className="space-y-1.5 mt-1.5">
                {inactive.map((o) => <Row key={o.id} o={o} cancelled={true} />)}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Bestätigung: Auftrag endgültig löschen */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="order-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400" /> Auftrag endgültig löschen
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Dieser Auftrag wird dauerhaft entfernt und erscheint nach Neuladen oder Migration nicht wieder.
              Ein verknüpftes Angebot bleibt bestehen (nur die Verknüpfung wird gelöst).
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="bg-zinc-900/50 border border-zinc-800 p-3 space-y-1 text-xs" data-testid="order-delete-details">
              <div className="flex justify-between"><span className="text-zinc-400">Kunde</span><span className="text-zinc-100">{deleteTarget.kunde || "Kunde noch nicht zugeordnet"}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Projekt</span><span className="text-zinc-100">{deleteTarget.projekt || "–"}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Annahmedatum</span><span className="font-mono text-zinc-100">{fmtDate(deleteTarget.decision_date)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Auftragswert</span><span className="font-mono text-zinc-100">{fmtEuro(deleteTarget.auftragswert)}</span></div>
            </div>
          )}
          {deleteTarget?.entry_id && (
            <div className="flex gap-2 items-start bg-amber-950/20 border border-amber-800/40 p-2.5" data-testid="order-delete-points-warning">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-300/90">
                Dieser Auftrag stammt aus dem Tageseintrag vom {fmtDate(deleteTarget.entry_date || deleteTarget.decision_date)}.
                Beim Löschen wird die Tageszusage um 1 reduziert und die Tagespunkte (−100) entsprechend angepasst.
                Zeiten, Zahlungen und übrige Tagesdaten bleiben unverändert.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="h-9 text-xs" onClick={() => setDeleteTarget(null)} data-testid="order-delete-cancel">Abbrechen</Button>
            <Button className="h-9 text-xs bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="order-delete-confirm">Endgültig löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
